import React, { useState, useEffect, useCallback, useMemo } from 'react';
import firebase from '../../../config/firebase.config';
import {
  addNotification,
  notificationStates,
} from '../../../components/Notification';
import { importQuickBooksEmployees } from '../../../services/quickbooks';
import { importSalesforceEmployees } from '../../../services/salesforce';
import { importSageHREmployees } from '../../../services/sagehr';
import { importZohoPeopleEmployees } from '../../../services/zohoPeople';
import { IntegrationImportTabView } from './IntegrationImportTab.view';

export const IntegrationImportTab = ({ companyId, teamId, userId, source }) => {
  const [logMeta, setLogMeta] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [reimporting, setReimporting] = useState(false);

  const integrationDocId =
    source === 'quickbooks'
      ? 'quickbooksTime'
      : source === 'sagehr'
        ? 'sagehr'
        : source === 'zohopeople'
          ? 'zohoPeople'
          : 'salesforce';

  const sourceName =
    source === 'quickbooks'
      ? 'QuickBooks'
      : source === 'sagehr'
        ? 'Sage HR'
        : source === 'zohopeople'
          ? 'Zoho People'
          : 'Salesforce';

  useEffect(() => {
    if (!companyId) {
      setLogMeta(null);
      setEmployees([]);
      return;
    }

    setLoading(true);
    setLogMeta(null);
    setEmployees([]);

    const unsub = firebase
      .firestore()
      .collection('companyDatabase')
      .doc(companyId)
      .collection('integrations')
      .doc(integrationDocId)
      .collection('importLogs')
      .orderBy('importedAt', 'desc')
      .limit(1)
      .onSnapshot(
        async (snap) => {
          if (snap.empty) {
            setLogMeta(null);
            setEmployees([]);
            setLoading(false);
            return;
          }

          const d = snap.docs[0];
          const data = d.data();
          setLogMeta({
            id: d.id,
            importedAt: data.importedAt?.toDate?.() ?? null,
            importedBy: data.importedBy || {},
            status: data.status || 'completed',
            stats: data.stats || {},
          });

          try {
            const detailDoc = await firebase
              .firestore()
              .collection('companyDatabase')
              .doc(companyId)
              .collection('integrations')
              .doc(integrationDocId)
              .collection('importLogs')
              .doc(d.id)
              .collection('details')
              .doc('data')
              .get();
            setEmployees(
              detailDoc.exists ? detailDoc.data().employees || [] : []
            );
          } catch (err) {
            console.error('Failed to load import details', err);
            setEmployees([]);
          } finally {
            setLoading(false);
          }
        },
        (err) => {
          console.error('Import log subscription error', err);
          setLoading(false);
        }
      );

    return unsub;
  }, [companyId, integrationDocId]);

  const counts = useMemo(() => {
    const c = {
      all: employees.length,
      imported: 0,
      invited: 0,
      mapped: 0,
      skipped: 0,
      error: 0,
    };
    for (const e of employees) {
      if (c[e.outcome] !== undefined) c[e.outcome]++;
    }
    return c;
  }, [employees]);

  const filteredEmployees = useMemo(() => {
    if (statusFilter === 'all') return employees;
    return employees.filter((e) => e.outcome === statusFilter);
  }, [employees, statusFilter]);

  const handleReimport = useCallback(async () => {
    if (!teamId) {
      addNotification({
        message: 'Select a department before re-importing.',
        level: notificationStates.error,
      });
      return;
    }
    setReimporting(true);
    try {
      const fn =
        source === 'quickbooks'
          ? importQuickBooksEmployees
          : source === 'sagehr'
            ? importSageHREmployees
            : source === 'zohopeople'
              ? importZohoPeopleEmployees
              : importSalesforceEmployees;
      const result = await fn(companyId, teamId, userId);
      const parts = [];
      if (result.imported > 0) parts.push(`${result.imported} created`);
      if (result.invited > 0) parts.push(`${result.invited} invited`);
      if (result.mapped > 0) parts.push(`${result.mapped} linked`);
      if (result.errors > 0) parts.push(`${result.errors} failed`);
      addNotification({
        message: `Import complete: ${parts.join(', ') || 'nothing new'}.`,
        level:
          result.errors > 0
            ? notificationStates.warning
            : notificationStates.success,
      });
    } catch {
      addNotification({
        message: `Re-import failed. Please try again.`,
        level: notificationStates.error,
      });
    } finally {
      setReimporting(false);
    }
  }, [companyId, teamId, userId, source]);

  return (
    <IntegrationImportTabView
      companyId={companyId}
      teamId={teamId}
      source={source}
      sourceName={sourceName}
      loading={loading}
      logMeta={logMeta}
      counts={counts}
      statusFilter={statusFilter}
      onStatusFilterChange={setStatusFilter}
      filteredEmployees={filteredEmployees}
      reimporting={reimporting}
      onReimport={handleReimport}
    />
  );
};
